import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useValidarTokenSenha, useRedefinirSenha } from '@/services/Auth';
import {
  RedefinirSenhaFormData,
  redefinirSenhaValidationSchema,
} from '../validators/redefinirSenhaValidator';

type Estado = 'validando' | 'valido' | 'invalido' | 'concluido';

/**
 * Serve os dois motivos do mesmo token (`Proposito` no backend é só rótulo):
 * "esqueci minha senha" e o convite de primeiro acesso caem na MESMA tela —
 * os dois terminam com "defina uma senha nova".
 */
export function RedefinirSenha() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token') ?? '';

  const [estado, setEstado] = useState<Estado>('validando');
  const [erro, setErro] = useState('');
  const validarToken = useValidarTokenSenha();
  const redefinir = useRedefinirSenha();

  const { handleSubmit, control, formState } = useForm<RedefinirSenhaFormData>({
    resolver: zodResolver(redefinirSenhaValidationSchema),
    mode: 'onSubmit',
    defaultValues: { senha: '', confirmarSenha: '' },
  });
  const { errors } = formState;

  useEffect(() => {
    if (!email || !token) {
      setEstado('invalido');
      return;
    }

    validarToken.mutate(
      { Email: email, Token: token },
      {
        onSuccess: response => setEstado(response.status.resultado ? 'valido' : 'invalido'),
        onError: () => setEstado('invalido'),
      }
    );
    // Só na carga inicial da tela -- não revalidar a cada digitação do form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, token]);

  function concluir(dados: RedefinirSenhaFormData) {
    setErro('');
    redefinir.mutate(
      { Email: email, Token: token, NovaSenha: dados.senha },
      {
        onSuccess: response => {
          if (response.status.resultado) {
            setEstado('concluido');
          } else {
            // Mensagem genérica de propósito, ver RecuperacaoSenhaService no
            // backend -- não distingue token errado de token expirado.
            setErro(response.status.mensagem || 'Link inválido ou expirado.');
            setEstado('invalido');
          }
        },
        onError: () => setErro('Não foi possível concluir. Tente novamente.'),
      }
    );
  }

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-linear-to-br from-[#9ebe3b] via-[#7da32e] to-[#6b8e28] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col items-center justify-center p-16 relative z-10">
        <div className="animate-fade-in">
          <h1 className="text-5xl xl:text-6xl font-extrabold text-white mb-4 leading-tight drop-shadow-lg">
            Defina sua nova senha
          </h1>
          <p className="text-lg text-white/90 leading-relaxed font-normal">
            Escolha uma senha forte para proteger sua conta.
          </p>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center p-8 relative z-20 bg-white">
        <img
          src="/logo-it-works.png"
          alt="IT Works Logo"
          className="w-[270px] h-auto mb-8 animate-fade-in"
        />

        <div className="bg-white rounded-3xl p-8 sm:p-12 w-full max-w-[460px] shadow-[0_20px_60px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)] animate-fade-in">
          {estado === 'validando' && (
            <p className="text-center text-sm text-zinc-500">Verificando link...</p>
          )}

          {estado === 'invalido' && (
            <div className="flex flex-col items-center gap-4 text-center">
              <XCircle className="h-10 w-10 text-destructive" />
              <h2 className="text-xl font-bold text-[#2d3748]">Link inválido ou expirado</h2>
              <p className="text-sm text-zinc-500">
                {erro || 'Peça um novo link de redefinição de senha.'}
              </p>
              <Link to="/esqueci-senha" className="text-sm text-[#7da32e] hover:underline">
                Solicitar novo link
              </Link>
            </div>
          )}

          {estado === 'concluido' && (
            <div className="flex flex-col items-center gap-4 text-center">
              <CheckCircle2 className="h-10 w-10 text-[#7da32e]" />
              <h2 className="text-xl font-bold text-[#2d3748]">Senha definida com sucesso</h2>
              <p className="text-sm text-zinc-500">Você já pode entrar com a sua senha nova.</p>
              <Button className="w-full" onClick={() => navigate('/login')}>
                Ir para o login
              </Button>
            </div>
          )}

          {estado === 'valido' && (
            <>
              <div className="mb-8 text-center">
                <h2 className="text-2xl font-bold bg-linear-to-br from-[#2d3748] to-[#1a202c] bg-clip-text text-transparent mb-2">
                  Nova senha
                </h2>
                <p className="text-sm text-zinc-500">Defina a senha da sua conta ({email}).</p>
              </div>

              <form onSubmit={handleSubmit(concluir)} className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <label htmlFor="senha" className="text-sm font-semibold text-[#4a5568]">
                    Senha nova
                  </label>
                  <Controller
                    name="senha"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="senha"
                        type="password"
                        autoFocus
                        autoComplete="new-password"
                        {...field}
                      />
                    )}
                  />
                  {errors.senha?.message && (
                    <span className="text-[#e53e3e] text-xs font-medium">
                      {errors.senha.message}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="confirmarSenha" className="text-sm font-semibold text-[#4a5568]">
                    Confirmar senha
                  </label>
                  <Controller
                    name="confirmarSenha"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="confirmarSenha"
                        type="password"
                        autoComplete="new-password"
                        {...field}
                      />
                    )}
                  />
                  {errors.confirmarSenha?.message && (
                    <span className="text-[#e53e3e] text-xs font-medium">
                      {errors.confirmarSenha.message}
                    </span>
                  )}
                </div>

                {erro && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {erro}
                  </div>
                )}

                <Button type="submit" className="w-full h-12" disabled={redefinir.isPending}>
                  {redefinir.isPending ? 'Salvando...' : 'Definir senha'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
